import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BookOpen, Plus, X, Pencil, Trash2, Video, Upload, Play, ThumbsUp, MessageCircle, User } from 'lucide-react';
import { validateMediaFile } from '../../utils/chunkedUpload';
import { STORY_CATEGORIES } from './spiritualConstants';
import { CommentSection } from './CommentSection';

export function StoriesTab({
  stories, showStoryForm, setShowStoryForm, storyForm, setStoryForm,
  storyVideo, setStoryVideo, uploadingVideo, videoUploadProgress, videoError, setVideoError,
  editingStory, setEditingStory,
  onCreateStory, onUpdateStory, onLikeStory, onEditStory, onDeleteStory, token,
}) {
  const resetStoryForm = () => {
    setShowStoryForm(false);
    setEditingStory(null);
    setStoryForm({ title: '', content: '', category: 'other', featured_family_member: '', time_period: '', tags: '' });
  };

  return (
    <>
      {showStoryForm ? (
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              {editingStory ? 'Edit Story' : 'Share a Family Story'}
              <Button variant="ghost" size="sm" onClick={resetStoryForm}>
                <X className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editingStory ? onUpdateStory : onCreateStory} className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Title *</label>
                <Input
                  value={storyForm.title}
                  onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="story-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Category</label>
                <Select value={storyForm.category} onValueChange={(val) => setStoryForm({ ...storyForm, category: val })}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STORY_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Featured Family Member</label>
                  <Input value={storyForm.featured_family_member} onChange={(e) => setStoryForm({ ...storyForm, featured_family_member: e.target.value })} placeholder="e.g., Grandma Rose" className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Time Period</label>
                  <Input value={storyForm.time_period} onChange={(e) => setStoryForm({ ...storyForm, time_period: e.target.value })} placeholder="e.g., 1960s" className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Story *</label>
                <textarea value={storyForm.content} onChange={(e) => setStoryForm({ ...storyForm, content: e.target.value })} required rows={6} className="w-full bg-slate-900 border border-slate-600 rounded-md p-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500" placeholder="Share your family story..." data-testid="story-content-input" />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Tags (comma-separated)</label>
                <Input value={storyForm.tags} onChange={(e) => setStoryForm({ ...storyForm, tags: e.target.value })} placeholder="e.g., recipe, tradition, summer" className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" />
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-2 flex items-center gap-1">
                  <Video className="h-4 w-4" /> Attach a Video (optional, max 200MB / 5 min)
                </label>
                {storyVideo ? (
                  <div className="bg-slate-900 border border-slate-600 rounded-md p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Play className="h-4 w-4 text-amber-400" />
                        <span className="truncate max-w-[200px]">{storyVideo.name}</span>
                        <span className="text-slate-500">({(storyVideo.size / (1024 * 1024)).toFixed(1)}MB)</span>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setStoryVideo(null); setVideoError(null); }} className="text-slate-400 hover:text-red-400">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {uploadingVideo && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-slate-400 mb-1">
                          <span>Uploading video...</span>
                          <span>{videoUploadProgress}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${videoUploadProgress}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="bg-slate-900 border-2 border-dashed border-slate-600 rounded-md p-4 text-center hover:border-amber-500/50 transition">
                      <Upload className="h-6 w-6 text-slate-500 mx-auto mb-1" />
                      <p className="text-slate-400 text-sm">Click to attach a video (MP4, MOV, WebM up to 200MB, max 5 min)</p>
                      <p className="text-slate-500 text-xs mt-1">For best results, record in 1080p instead of 4K</p>
                    </div>
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setVideoError(null);
                        try {
                          const result = await validateMediaFile(f);
                          if (!result.valid) { setVideoError(result.error); return; }
                          setStoryVideo(f);
                        } catch {
                          setVideoError('Could not validate video. Please try again.');
                        }
                      }}
                      data-testid="story-video-input"
                    />
                  </label>
                )}
                {videoError && <p className="mt-2 text-red-400 text-sm" data-testid="video-error">{videoError}</p>}
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={uploadingVideo} data-testid="submit-story-btn">
                  {uploadingVideo ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> Uploading Video...</>)
                    : editingStory ? (<><Pencil className="h-4 w-4 mr-2" /> Update Story</>)
                      : (<><BookOpen className="h-4 w-4 mr-2" /> Share Story</>)}
                </Button>
                <Button type="button" variant="outline" onClick={resetStoryForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowStoryForm(true)} className="bg-amber-600 hover:bg-amber-700 mb-6" data-testid="add-story-btn">
          <Plus className="h-4 w-4 mr-2" /> Share a Story
        </Button>
      )}

      <div className="space-y-4">
        {stories.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-8 text-center">
              <BookOpen className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No stories yet. Share a family memory!</p>
            </CardContent>
          </Card>
        ) : stories.map(story => (
          <Card key={story.story_id} className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-white">{story.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-slate-400 mt-1">
                    <User className="h-3 w-3" />
                    <span>{story.author_name}</span>
                    <span>•</span>
                    <span>{new Date(story.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <Badge className="bg-slate-700">
                  {STORY_CATEGORIES.find(c => c.value === story.category)?.icon} {STORY_CATEGORIES.find(c => c.value === story.category)?.label}
                </Badge>
              </div>
              {story.featured_family_member && (
                <p className="text-amber-400 text-sm mb-2">About: {story.featured_family_member} {story.time_period && `(${story.time_period})`}</p>
              )}
              <p className="text-slate-300 whitespace-pre-wrap">{story.content}</p>

              {story.video_url && (
                <div className="mt-4 rounded-lg overflow-hidden bg-black" data-testid={`story-video-${story.story_id}`}>
                  <video src={story.video_url} controls preload="metadata" className="w-full max-h-[400px]" />
                </div>
              )}
              {story.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {story.tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-slate-400 border-slate-600 text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-700">
                <Button variant="ghost" size="sm" onClick={() => onLikeStory(story.story_id)} className="text-slate-400 hover:text-amber-400">
                  <ThumbsUp className="h-4 w-4 mr-1" /> {story.likes || 0}
                </Button>
                <span className="text-slate-500 text-sm flex items-center">
                  <MessageCircle className="h-4 w-4 mr-1" /> {story.comments_count || 0} comments
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onEditStory(story)} className="text-slate-400 hover:text-blue-400" data-testid={`edit-story-${story.story_id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDeleteStory(story.story_id)} className="text-slate-400 hover:text-red-400" data-testid={`delete-story-${story.story_id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
              <CommentSection itemType="story" itemId={story.story_id} token={token} />
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
